import React from 'react';
import { Box, Button, Grid, Paper, SelectChangeEvent, TextField } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { DeveloperUserIdSearch, MultiStatusSelect, SearchActionButtons, SearchResultCounter, SearchTextField, StatusSelect } from '../common';
import NormalSearchFields from './NormalSearchFields';
import TrainingSearchFields from './TrainingSearchFields';
import { ASSIGN_STATUS_DISPLAY, RESIGNED_STATUS_DISPLAY, ROLE_TYPE_OPTIONS, SENIOR_TALK_ROOM_STATUS_DISPLAY } from '../../types/nurse';
import { formatDateToISO } from '../../utils/dateUtils';

interface NurseSearchFormProps {
  trainingMode: boolean;
  // 共通フィールド
  searchTerm: string;
  onSearchTermChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearSearchField: () => void;
  emailSearchTerm: string;
  onEmailSearchTermChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearEmailField: () => void;
  userIdSearchTerm?: string;
  onUserIdSearchTermChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearUserIdField?: () => void;
  selectedRoles: string[];
  onRoleChange: (event: any) => void;
  // 通常モードフィールド
  talkStatus: string;
  onTalkStatusChange: (event: SelectChangeEvent) => void;
  assignStatus: string;
  onAssignStatusChange: (event: SelectChangeEvent) => void;
  blockedStatus: string;
  onBlockedStatusChange: (event: SelectChangeEvent) => void;
  conversationCount: string;
  onConversationCountChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  conversationCountOperator: string;
  onConversationCountOperatorChange: (event: SelectChangeEvent) => void;
  // 研修モードフィールド
  selectedWorkshopStatuses: string[];
  onWorkshopStatusChange: (event: any) => void;
  workshopFinishCount: string;
  onWorkshopFinishCountChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  workshopFinishCountOperator: string;
  onWorkshopFinishCountOperatorChange: (event: SelectChangeEvent) => void;
  // 共有フィールド
  resignedStatus: string;
  onResignedStatusChange: (event: SelectChangeEvent) => void;
  testAccountStatus: string;
  onTestAccountStatusChange: (event: SelectChangeEvent) => void;
  // 登録日範囲検索フィールド
  createdAtStart: string | null;
  onCreatedAtStartChange: (date: string | null) => void;
  createdAtEnd: string | null;
  onCreatedAtEndChange: (date: string | null) => void;
  // サポート関係フィールド
  seniorTalkRoomStatus: string;
  onSeniorTalkRoomStatusChange: (event: SelectChangeEvent) => void;
  // 検索操作
  totalCount: number;
  onSearch: (event: React.FormEvent) => void;
  onClear: () => void;
  onCSVExport?: () => void;
}

/**
 * 看護師検索フォーム
 */
const NurseSearchForm: React.FC<NurseSearchFormProps> = ({
  trainingMode,
  // 共通フィールド
  searchTerm, onSearchTermChange, onClearSearchField,
  emailSearchTerm, onEmailSearchTermChange, onClearEmailField,
  userIdSearchTerm, onUserIdSearchTermChange, onClearUserIdField,
  selectedRoles, onRoleChange,
  // 通常モードフィールド
  talkStatus, onTalkStatusChange,
  assignStatus, onAssignStatusChange,
  blockedStatus, onBlockedStatusChange,
  conversationCount, onConversationCountChange,
  conversationCountOperator, onConversationCountOperatorChange,
  // 研修モードフィールド
  selectedWorkshopStatuses, onWorkshopStatusChange,
  workshopFinishCount, onWorkshopFinishCountChange,
  workshopFinishCountOperator, onWorkshopFinishCountOperatorChange,
  // 共有フィールド
  resignedStatus, onResignedStatusChange,
  testAccountStatus, onTestAccountStatusChange,
  // 登録日範囲検索フィールド
  createdAtStart, onCreatedAtStartChange,
  createdAtEnd, onCreatedAtEndChange,
  // サポート関係フィールド
  seniorTalkRoomStatus, onSeniorTalkRoomStatusChange,
  // 検索操作
  totalCount, onSearch, onClear, onCSVExport
}) => {
  return (
    <Paper
      component="form"
      elevation={2}
      sx={{ p: 3, mb: 3, mt: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
      onSubmit={onSearch}
    >
      {/* 1段目: ロール、名前・ニックネーム、メールアドレス、退会状態 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <MultiStatusSelect
            label="ロール"
            id="role-filter"
            value={selectedRoles}
            onChange={onRoleChange}
            options={ROLE_TYPE_OPTIONS}
            emptyLabel="すべて"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <SearchTextField
            label="名前 or ニックネーム"
            value={searchTerm}
            onChange={onSearchTermChange}
            onClear={onClearSearchField}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <SearchTextField
            label="メールアドレス"
            value={emailSearchTerm}
            onChange={onEmailSearchTermChange}
            onClear={onClearEmailField}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <StatusSelect
            label="退会状態"
            id="resigned-status-filter"
            value={resignedStatus}
            onChange={onResignedStatusChange}
            options={RESIGNED_STATUS_DISPLAY}
          />
        </Grid>
      </Grid>

      {/* 2列目: モード別検索フィールド */}
      {trainingMode ? (
        <TrainingSearchFields
          selectedWorkshopStatuses={selectedWorkshopStatuses}
          onWorkshopStatusChange={onWorkshopStatusChange}
          workshopFinishCount={workshopFinishCount}
          onWorkshopFinishCountChange={onWorkshopFinishCountChange}
          workshopFinishCountOperator={workshopFinishCountOperator}
          onWorkshopFinishCountOperatorChange={onWorkshopFinishCountOperatorChange}
          testAccountStatus={testAccountStatus}
          onTestAccountStatusChange={onTestAccountStatusChange}
        />
      ) : (
        <NormalSearchFields
          talkStatus={talkStatus}
          onTalkStatusChange={onTalkStatusChange}
          blockedStatus={blockedStatus}
          onBlockedStatusChange={onBlockedStatusChange}
          conversationCount={conversationCount}
          onConversationCountChange={onConversationCountChange}
          conversationCountOperator={conversationCountOperator}
          onConversationCountOperatorChange={onConversationCountOperatorChange}
          testAccountStatus={testAccountStatus}
          onTestAccountStatusChange={onTestAccountStatusChange}
        />
      )}

      {/* 登録日範囲フィルターと開発者モードUser ID検索 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
            {/* 開始日入力 */}
            <TextField
              label="登録日(開始)"
              type="date"
              size="small"
              value={createdAtStart || ''}
              onChange={(e) => {
                const newStartDate = e.target.value || null;
                onCreatedAtStartChange(newStartDate);
                // 開始日が変更されたとき、終了日のチェック
                // もし終了日が設定済みで、新しい開始日より前の日付なら終了日をクリア
                if (newStartDate && createdAtEnd && createdAtEnd < newStartDate) {
                  onCreatedAtEndChange(null);
                }
              }}
              InputLabelProps={{
                shrink: true,
              }}
              // 本日までの日付しか選べないようにする
              inputProps={{
                max: formatDateToISO(new Date()) // 本日の日付
              }}
              sx={{ width: '50%', mr: 1 }}
            />

            {/* 終了日入力 */}
            <TextField
              label="登録日(終了)"
              type="date"
              size="small"
              value={createdAtEnd || ''}
              onChange={(e) => onCreatedAtEndChange(e.target.value || null)}
              InputLabelProps={{
                shrink: true,
              }}
              // 開始日が入力されている場合のみ有効化（登録日(終了)のみの検索はサポートしない）
              disabled={!createdAtStart}
              // 開始日以降、本日までの日付しか選べないようにする
              inputProps={{
                min: createdAtStart || undefined,
                max: formatDateToISO(new Date()) // 本日の日付
              }}
              sx={{ width: '50%' }}
            />
          </Box>
        </Grid>

        {/* サポート関係フィルター - 通常モード・研修モード共通 */}
        <Grid item xs={12} md={3}>
          <StatusSelect
            label="サポート関係"
            id="senior-talk-room-status-filter"
            value={seniorTalkRoomStatus}
            onChange={onSeniorTalkRoomStatusChange}
            options={SENIOR_TALK_ROOM_STATUS_DISPLAY}
          />
        </Grid>

        {/* 受付状態（通常モードのみ）*/}
        {!trainingMode && (
          <Grid item xs={12} md={3}>
            <StatusSelect
              label="受付状態"
              id="assign-status-filter"
              value={assignStatus}
              onChange={onAssignStatusChange}
              options={ASSIGN_STATUS_DISPLAY}
            />
          </Grid>
        )}

        {/* 開発者モード: User ID検索 */}
        {userIdSearchTerm !== undefined && onUserIdSearchTermChange && onClearUserIdField && (
          <DeveloperUserIdSearch
            userIdSearchTerm={userIdSearchTerm}
            onUserIdSearchTermChange={onUserIdSearchTermChange}
            onClearUserIdField={onClearUserIdField}
          />
        )}
      </Grid>

      {/* 3列目: 件数表示、検索ボタン、クリアボタン、CSVダウンロード - シャドウなし */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, position: 'relative' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SearchResultCounter count={totalCount}/>
          {onCSVExport && (
            <Button
              type="button"
              variant="outlined"
              color="info"
              size="small"
              startIcon={<DownloadIcon/>}
              onClick={onCSVExport}
            >
              ダウンロード
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, position: 'absolute', right: 0 }}>
          <SearchActionButtons
            onClear={onClear}
            searchLabel="検索"
            clearLabel="クリア"
          />
        </Box>
      </Box>
    </Paper>
  );
};
export default NurseSearchForm;
